(ns dre.widgets.demographics.events
  (:require
   [dre.fx :as dre.fx]
   [clojure.string :as str]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.i18n.views :refer [tr]]
   [dre.network :as-alias network]
   [dre.pages.search.events :as search-events]
   [dre.page-util :refer [capitalize-words]]
   [dre.utils :refer [filter-map]]
   [dre.widgets.analysis.tag-distribution :as tag-distribution-events]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]
   [dre.i18n.utils :as i18n-utils]))

;; extension of analysis/tag_distribution

(def id
  :demographics)

(def tag->sense
  {:age/in-utero "SUGhHB"
   :age/newborn "SUFMq"
   :age/infant "SUFMX"
   :age/child "SUB98"
   :age/adolescent "SUYr"
   :age/adult "SUaM"
   ;; :age/young-adult "SU6QH"
   :age/elderly "SUcB"
   :gender/male "SUVI3"
   :gender/female "SUVEQ"
   :ethnicity/caucasians "S2B0i"
   :ethnicity/american "SUCRhn"})

(defn ->pill
  [term]
  {:pill/type :term
   :pill/category (keyword (namespace term))
   :pill/query (or (tag->sense term) (name term))
   :pill/label (capitalize-words (name term))})

(defn prepare-data
  [data filters key filter-key]
  (let [data (filter-map data #(= (namespace %) key))
        f (get filters filter-key)
        click (fn [filter-key term]
                #(rf/dispatch [::search-events/widget-filter-select
                               filter-key (->pill term)]))]
    (map (fn [[term count]]
           (let [pill (->pill term)
                 f? (contains? f pill)
                 label (:pill/label pill)]
             (hash-map
              :label label
              :display-label (:pill/display-label pill)
              :term term
              :class [(when (seq f) (if f? "active" "inactive"))]
              :on-click (click filter-key term)
              :count count)))
         data)))

;; -- Events

(rf/reg-event-fx
 ::export
 [rf/trim-v]
 (fn-traced
  [cofx _]
  (let [root-db (:db cofx)
        i18n-db (:i18n root-db)
        db (get-in root-db tag-distribution-events/db-path)
        data (get-in db [:data id])
        age (prepare-data data [] "age" :demographics)
        age (sort-by :count > age)
        gender (prepare-data data [] "gender" :demographics)
        gender (sort-by :count > gender)
        ethnicity (prepare-data data [] "ethnicity" :demographics)
        ethnicity (sort-by :count > ethnicity)
        otherp (prepare-data data [] "otherp" :demographics)
        otherp (sort-by :count > otherp)
        csv (str/join
             "\n"
             (concat
              [(str (i18n-utils/translate i18n-db "Demographics type") ", "
                    (i18n-utils/translate i18n-db "Category") ", "
                    (i18n-utils/translate i18n-db "Count"))]
              (map (fn [{:keys [:label :count]}]
                     (str (i18n-utils/translate i18n-db "Gender") ",\""
                          (str/replace label "\"" "\"\"") "\"," count))
                   gender)
              (map (fn [{:keys [:label :count]}]
                     (str (i18n-utils/translate i18n-db "Age group") ",\""
                          (str/replace label "\"" "\"\"") "\"," count))
                   age)
              (map (fn [{:keys [:label]}]
                     (str (i18n-utils/translate i18n-db "Ethnicity") " & "
                          (i18n-utils/translate i18n-db "Race")
                          ",\"" (str/replace label "\"" "\"\"") "\","))
                   ethnicity)
              (map (fn [{:keys [:label]}]
                     (str (i18n-utils/translate i18n-db "Other") ",\""
                          (str/replace label "\"" "\"\"") "\","))
                   otherp)))]
    {::dre.fx/download {:content csv
                        :filename "demographics.csv"
                        :mimetype "text/csv"}})))


;; -- Subscriptions

(rf/reg-sub
  ::state
  (fn [db _]
    (get-in db tag-distribution-events/db-path)))


(rf/reg-sub
  ::status
  :<- [::state]
  (fn [db _]
    (:state db)))

(rf/reg-sub
 ::data
 :<- [::state]
 :<- [::search-events/widget-filters]
 (fn [[db widget-filters] _]
   (let [data (get-in db [:data id])
         age (prepare-data data widget-filters "age" :demographics)
         gender (prepare-data data widget-filters "gender" :demographics)
         ethnicity (prepare-data data widget-filters "ethnicity" :demographics)
         otherp (prepare-data data widget-filters "otherp" :demographics)
         tbl (keep
              identity
              (concat
               [(when (seq gender)
                  {:label [:span.separator [tr "Gender"]]
                   :term "divider"
                   :asis true
                   :style {:font-weight "bold"}})]
               (reverse (sort-by :count gender))
               [(when (seq age)
                  {:label [:span.separator [tr "Age Group"]]
                   :term "divider"
                   :asis true
                   :style {:font-weight "bold"}})]
               (reverse (sort-by :count age))
               [(when (seq ethnicity)
                  {:label [:span.separator [tr "Ethnicity and Race"]]
                   :term "divider"
                   :asis true
                   :style {:font-weight "bold"}})]
               (map #(dissoc % :count)
                    (reverse (sort-by :count ethnicity)))
               [(when (seq otherp)
                  {:label [:span.separator [tr "Other"]]
                   :term "divider"
                   :asis true
                   :style {:font-weight "bold"}})]
               (map #(dissoc % :count)
                    (reverse (sort-by :count otherp)))))]
     (filter :label tbl))))

;; 
(defn- gender-pill?
  "Checks whether the pill is a gender pill or not."
  [{:pill/keys [category]}]
  (= category :gender))

(defn gender-filters
  [db]
  (->> db
       search-events/db-path
       :widget-filters
       id
       (filter gender-pill?)))

(defn gender-queries
  [db]
  (->> db
       search-events/db-path
       :search-query
       :query/pills
       (filter gender-pill?)))

(defn- all-pill?
  "Checks whether the pill is a demograhics pill or not."
  [{:pill/keys [category]}]
  (or (= category :gender) (= category :age) (= category :ethnicity) (= category :otherp)))

(defn all-filters
  [db]
  (->> db
       search-events/db-path
       :widget-filters
       id
       (filter all-pill?)))

(defn all-queries
  [db]
  (->> db
       search-events/db-path
       :search-query
       :query/pills
       (filter all-pill?)))

(defn selected-demographics?
  [db]
  (let [x (concat (all-queries db) (all-filters db))]
    (not-empty x)))

(rf/reg-sub
 ::used-in-search?
 selected-demographics?)